import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { ChevronLeft, ChevronRight, FileQuestion, ImageOff } from "lucide-react";
import { ApiError } from "../../api/client";
import { catalogueApi, type Plate, type PlateListing, type PlatePosition } from "../../api/catalogue";
import { Breadcrumb } from "../../components/catalogue/Breadcrumb";
import { DiagramViewer, type DiagramMarker } from "../../components/catalogue/DiagramViewer";
import { EmptyState, LoadError } from "../../components/catalogue/Notices";
import { PageWidth } from "../../components/catalogue/StoreShell";
import { BottomSheet } from "../../components/catalogue/plate/BottomSheet";
import { PartPanel, VersionChooser } from "../../components/catalogue/plate/PartPanel";
import { PlatePartsList } from "../../components/catalogue/plate/PlatePartsList";
import { PlatePartsTable } from "../../components/catalogue/plate/PlatePartsTable";
import { findPosition, plateRows, rowKey, type PlateRow } from "../../components/catalogue/plate/plateRows";
import { Icon } from "../../components/ui";
import { cx } from "../../components/ui/cx";
import { useMediaQuery } from "../../lib/useMediaQuery";
import { useAddToBasket } from "../cart/useCart";
import { usePageTitle } from "./usePageTitle";
import { NotFound } from "./NotFoundRoute";

type Selection = { positionId: number; listingId: number | null };

/** PRD screen 4: the diagram and its parts list, the page the whole catalogue leads to. */
export function AssemblyRoute() {
  const id = Number(useParams().id);
  const plate = useQuery({ queryKey: ["catalogue", "plate", id], queryFn: () => catalogueApi.plate(id) });
  usePageTitle(plate.data ? `${plate.data.code} ${plate.data.name}` : null);

  if (plate.isPending) return <PageWidth className="py-10 type-body text-text-muted">Loading the diagram…</PageWidth>;
  if (plate.isError) {
    if (plate.error instanceof ApiError && plate.error.status === 404) return <NotFound />;
    return (
      <PageWidth className="py-10">
        <LoadError onRetry={() => plate.refetch()} />
      </PageWidth>
    );
  }
  // Keyed by plate, so moving to the next plate starts with nothing selected.
  return <PlateScreen key={plate.data.id} plate={plate.data} />;
}

function PlateScreen({ plate }: { plate: Plate }) {
  const wide = useMediaQuery("(min-width: 1024px)");
  const [searchParams] = useSearchParams();
  const positions = plate.positions;
  const [selection, setSelection] = useState<Selection | null>(() => {
    // Arriving from a listing's "where it fits" (?pos=10) opens that position.
    const linked = findPosition(positions, searchParams.get("pos") ?? "");
    return linked ? { positionId: linked.id, listingId: null } : null;
  });
  const [jump, setJump] = useState("");
  const [view, setView] = useState<"diagram" | "list">("diagram");
  const listFrame = useRef<HTMLDivElement>(null);

  const { numbered, unnumbered } = useMemo(() => plateRows(positions), [positions]);
  const markers = useMemo<DiagramMarker[]>(
    () =>
      positions.flatMap((position) =>
        position.position_number === null
          ? []
          : position.markers.map((marker, index) => ({ key: `${position.id}-${index}`, position: position.id, label: position.position_number!, x: marker.x_percent, y: marker.y_percent })),
      ),
    [positions],
  );

  const position = selection ? positions.find((p) => p.id === selection.positionId) : undefined;
  // A position holding one part opens that part; several ask which (VersionChooser).
  const listing: PlateListing | undefined = position && (position.listings.find((l) => l.id === selection?.listingId) ?? (position.listings.length === 1 ? position.listings[0] : undefined));
  const selectedKey = position && listing ? rowKey(position, listing) : null;
  const close = useCallback(() => setSelection(null), []);

  const selectPosition = (positionId: number) => setSelection({ positionId, listingId: null });
  const pickRow = (row: PlateRow) => setSelection({ positionId: row.position.id, listingId: row.listing.id });

  // Keeps the chosen position's rows in view in the desktop list.
  useEffect(() => {
    if (!wide || !position || !listFrame.current) return;
    const index = numbered.findIndex((row) => row.position.id === position.id);
    listFrame.current.querySelectorAll("tbody tr")[index]?.scrollIntoView({ block: "nearest" });
  }, [wide, position, numbered]);

  const jumpTo = (typed: string) => {
    const found = findPosition(positions, typed);
    if (found) selectPosition(found.id);
    return found;
  };
  const jumpMissing = jump.trim() !== "" && !findPosition(positions, jump);
  const lastNumber = numbered.at(-1)?.position.position_number ?? "1";

  const siblingIndex = plate.siblings.findIndex((s) => s.id === plate.id);
  const partCount = positions.reduce((sum, p) => sum + p.listings.length, 0);
  const empty = positions.length === 0;

  const selectionContent =
    position &&
    (listing ? (
      <SelectedPart key={listing.id} position={position} listing={listing} onClose={close} touch={!wide} />
    ) : (
      <VersionChooser position={position} onPick={(l) => setSelection({ positionId: position.id, listingId: l.id })} onClose={close} touch={!wide} />
    ));

  const diagram = plate.diagram ? (
    <DiagramViewer
      image={plate.diagram}
      markers={markers}
      selected={position?.id ?? null}
      onSelect={selectPosition}
      touch={!wide}
      caption={wide ? `${plate.code} · ${plate.name}` : "Tap a number to see the part"}
      alt={`Exploded diagram of the ${plate.name}, ${plate.code}, with numbered positions`}
    />
  ) : (
    <EmptyState icon={ImageOff} title="This diagram isn't online yet">
      The parts list below is complete. Use the position numbers from your printed book.
    </EmptyState>
  );

  if (!wide) {
    return (
      <div className="pt-3">
        <PageWidth>
          <Link to={`/equipment-models/${plate.equipment_model.id}`} className="-ml-2 inline-flex min-h-11 items-center gap-1 border-b-0 type-label text-text-link">
            <Icon icon={ChevronLeft} size={20} /> {plate.equipment_model.name}
          </Link>
          <h1 className="mt-1 type-h2 [font-size:var(--text-xl)]">{plate.name}</h1>
          <p className="mt-1 mb-0 type-small text-text-muted">
            <span className="type-sku">{plate.code}</span> · {partCount} {partCount === 1 ? "part" : "parts"}
          </p>
        </PageWidth>
        {empty ? (
          <PageWidth className="mt-6">
            <NothingPublished />
          </PageWidth>
        ) : (
          <>
            <PageWidth className="mt-4 flex gap-3">
              <div role="group" aria-label="Show" className="flex flex-1">
                {(["diagram", "list"] as const).map((value, index) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={view === value}
                    onClick={() => setView(value)}
                    className={cx(
                      "h-(--control-lg) flex-1 cursor-pointer border font-display type-label [font-size:var(--text-sm)]",
                      index === 0 ? "rounded-l-md" : "-ml-px rounded-r-md",
                      view === value ? "border-green-700 bg-green-700 text-white" : "border-border-default bg-surface-card text-ink-600",
                    )}
                  >
                    {value === "diagram" ? "Diagram" : "Parts list"}
                  </button>
                ))}
              </div>
              <form
                onSubmit={(event: FormEvent) => {
                  event.preventDefault();
                  if (jumpTo(jump)) setJump("");
                }}
                className="flex flex-none"
              >
                <label className="sr-only" htmlFor="plate-jump">
                  Go to position
                </label>
                <input
                  id="plate-jump"
                  value={jump}
                  onChange={(event) => setJump(event.target.value)}
                  inputMode="text"
                  enterKeyHint="go"
                  autoComplete="off"
                  placeholder="Pos."
                  className="h-(--control-lg) w-20 rounded-md border border-border-default bg-surface-card px-3 text-center type-sku [font-size:var(--text-base)] outline-none focus:border-green-700 focus:shadow-focus"
                />
              </form>
            </PageWidth>
            <PageWidth>
              {jumpMissing && <p className="mt-2 mb-0 type-small text-text-muted">No position {jump.trim()} on this diagram. Numbers here run to {lastNumber}.</p>}
            </PageWidth>
            <PageWidth className="mt-3">
              {view === "diagram" ? (
                diagram
              ) : (
                <>
                  <PlatePartsList rows={numbered} selectedKey={selectedKey} onPick={pickRow} />
                  {unnumbered.length > 0 && (
                    <div className="mt-6">
                      <UnnumberedIntro count={unnumbered.length} />
                      <PlatePartsList rows={unnumbered} selectedKey={selectedKey} onPick={pickRow} />
                    </div>
                  )}
                </>
              )}
              <Footnote />
            </PageWidth>
          </>
        )}
        <BottomSheet open={Boolean(position)} onClose={close} label={position ? `Position ${position.position_number ?? ""}` : "Part"}>
          {selectionContent}
        </BottomSheet>
      </div>
    );
  }

  return (
    <PageWidth className={cx("pt-6", position && "pb-56")}>
      <Breadcrumb
        items={[
          { label: plate.equipment_category.name, to: "/equipment-categories" },
          { label: plate.equipment_model.name, to: `/equipment-models/${plate.equipment_model.id}` },
          { label: plate.name },
        ]}
      />

      <div className="mt-4 flex flex-wrap items-center gap-5 rounded-md border border-border-subtle bg-surface-card px-5 py-3 shadow-xs">
        <SiblingPager plate={plate} index={siblingIndex} />
        <span className="h-6 w-px bg-border-subtle" />
        <h1 className="m-0 min-w-0 type-h4 [font-size:var(--text-base)] text-text-strong">
          <span className="type-sku">{plate.code}</span> · {plate.name}
        </h1>
        {!empty && (
          <span className="ml-auto flex items-center gap-3">
            {/* Customers arrive knowing their number from the printed book: typing it beats scrolling. */}
            <label htmlFor="plate-jump" className="type-eyebrow tracking-[var(--tracking-wider)] text-ink-500 uppercase">
              Position
            </label>
            <input
              id="plate-jump"
              value={jump}
              onChange={(event) => {
                setJump(event.target.value);
                jumpTo(event.target.value);
              }}
              autoComplete="off"
              placeholder={lastNumber}
              aria-describedby={jumpMissing ? "plate-jump-missing" : undefined}
              className="h-(--control-sm) w-20 rounded-sm border border-border-default bg-surface-card px-2 text-center type-sku [font-size:var(--text-sm)] outline-none focus:border-green-700 focus:shadow-focus"
            />
            {jumpMissing && (
              <span id="plate-jump-missing" className="type-small text-text-muted">
                Not on this diagram
              </span>
            )}
          </span>
        )}
      </div>

      {empty ? (
        <div className="mt-6">
          <NothingPublished />
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-[minmax(0,55fr)_minmax(0,45fr)] items-start gap-6">
          <div className="min-w-0">{diagram}</div>
          <div className="min-w-0">
            <div ref={listFrame}>
              {/* The list scrolls on its own beside the plate, its headings pinned. */}
              <PlatePartsTable
                rows={numbered}
                selectedKey={selectedKey}
                selectedPosition={position?.id ?? null}
                onPick={pickRow}
                numbered
                className="max-h-[72vh] overflow-y-auto [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10"
              />
            </div>
            {unnumbered.length > 0 && (
              <div className="mt-6">
                <UnnumberedIntro count={unnumbered.length} />
                <PlatePartsTable rows={unnumbered} selectedKey={selectedKey} selectedPosition={null} onPick={pickRow} numbered={false} />
              </div>
            )}
            <Footnote />
          </div>
        </div>
      )}

      {selectionContent && (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 px-5 pb-5">
          <div className="pointer-events-auto mx-auto max-h-[60vh] max-w-(--container-xl) overflow-y-auto rounded-md bg-surface-card shadow-lg">
            {selectionContent}
          </div>
        </div>
      )}
    </PageWidth>
  );
}

/** The picked part, with its add-to-basket state. Keyed by listing, so picking another starts afresh. */
function SelectedPart({ position, listing, onClose, touch }: { position: PlatePosition; listing: PlateListing; onClose: () => void; touch: boolean }) {
  const basket = useAddToBasket(listing.id);
  return <PartPanel position={position} listing={listing} onClose={onClose} touch={touch} onAdd={basket.onAdd} addState={basket.state} />;
}

/** Steps through the machine's plates in the book's order: "2 of 14". */
function SiblingPager({ plate, index }: { plate: Plate; index: number }) {
  const navigate = useNavigate();
  const previous = plate.siblings[index - 1];
  const next = plate.siblings[index + 1];
  const step = (label: string, icon: typeof ChevronLeft, target?: { id: number; name: string }) => (
    <button
      type="button"
      aria-label={target ? `${label}: ${target.name}` : label}
      disabled={!target}
      onClick={() => target && navigate(`/assemblies/${target.id}`)}
      className="flex size-8 cursor-pointer items-center justify-center rounded-sm border border-border-default bg-white text-ink-700 hover:bg-paper-1 disabled:cursor-not-allowed disabled:opacity-40"
    >
      <Icon icon={icon} size={16} />
    </button>
  );
  return (
    <span className="flex items-center gap-2">
      {step("Previous diagram", ChevronLeft, previous)}
      <span className="type-sku [font-size:var(--text-sm)] whitespace-nowrap text-ink-600">
        {index + 1} of {plate.siblings.length}
      </span>
      {step("Next diagram", ChevronRight, next)}
    </span>
  );
}

function UnnumberedIntro({ count }: { count: number }) {
  return (
    <>
      <h2 className="mb-1 type-h4 [font-size:var(--text-sm)] text-text-strong">
        Listed, not numbered on the diagram <span className="type-small font-normal text-text-muted">· {count} {count === 1 ? "part" : "parts"}</span>
      </h2>
      <p className="mb-3 type-small text-text-muted">The book lists these without a number: usually a unit sold complete, or a part cut to length.</p>
    </>
  );
}

function Footnote() {
  return <p className="mt-5 mb-0 type-small text-text-muted">Names are as printed in the manufacturer's book. Prices include tax; delivery is quoted separately.</p>;
}

function NothingPublished() {
  return (
    <EmptyState icon={FileQuestion} title="No parts on this diagram are online yet">
      We're still adding them. Try another diagram for this machine.
    </EmptyState>
  );
}
